import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "@/lib/site-data";
import { scrollToSection, scrollToTop } from "@/lib/scroll";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (id === "home") scrollToTop();
    else scrollToSection(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
      className="fixed top-0 left-0 right-0 z-50"
      data-testid="navbar"
    >
      <div
        className={`transition-colors duration-500 ${
          scrolled ? "glass-strong border-b border-white/10" : "bg-transparent"
        }`}
      >
        <nav className="mx-auto flex h-20 max-w-[1440px] items-center justify-between px-6 md:px-12">
          <button
            onClick={() => go("home")}
            data-testid="logo-button"
            className="group flex items-center"
            aria-label="Yas Solutions home"
          >
            <img
              src={`${process.env.PUBLIC_URL || ""}/logo.png`}
              alt="Yas Solutions"
              className="h-9 w-auto transition-transform duration-300 group-hover:scale-[1.03] sm:h-11 md:h-14"
            />
          </button>

          {/* Desktop links */}
          <div className="hidden items-center gap-1 lg:flex">
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                onClick={() => go(link.id)}
                data-testid={`nav-link-${link.id}`}
                className="relative rounded-full px-4 py-2 text-sm font-medium text-white/70 transition-colors duration-300 hover:text-white"
              >
                {link.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => go("contact")}
              data-testid="nav-quote-button"
              className="group hidden items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-black transition-transform duration-300 hover:scale-[1.03] sm:flex"
            >
              Get a Free Quote
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>

            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white lg:hidden"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </nav>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="glass-strong border-b border-white/10 px-6 py-6">
              <div className="flex flex-col gap-1">
                {NAV_LINKS.map((link) => (
                  <button
                    key={link.id}
                    onClick={() => go(link.id)}
                    data-testid={`mobile-nav-link-${link.id}`}
                    className="rounded-xl px-4 py-3 text-left font-heading text-lg text-white/80 transition-colors hover:bg-white/5 hover:text-white"
                  >
                    {link.label}
                  </button>
                ))}
                <button
                  onClick={() => go("contact")}
                  data-testid="mobile-quote-button"
                  className="mt-3 flex items-center justify-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-semibold text-black"
                >
                  Get a Free Quote
                  <ArrowUpRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
